import { AssetClassesQuery, PacingQuarter, PacingType, Security_Status } from '@/__generated__/graphql';
import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { PlusIcon } from '@/assets/icons/info-icons/plus';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input, { ErrorText } from '@/components/basicComponents/input';
import { formatDollarInputValue, formatMaskedDollarInputValue } from '@/components/basicComponents/input/utils';
import Label from '@/components/basicComponents/label';
import RadioButton from '@/components/basicComponents/radioButton';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { GET_CURRENT_SUB_ASSET_CLASSES_QUERY, GET_CURRENT_TAGS_QUERY, UPDATE_INVESTMENT_MUTATION } from '@/components/dueDiligence-page/queries';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { GET_ASSET_CLASSES_QUERY } from '@/components/settings-page/assetClassDefaultTargets/queries';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { useGoBack } from '@/hooks/useGoBack';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { InvestmentPacingType, SECURITY_STATUS } from '@trace-8/trace8-schema';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { FormFieldsWrapper } from '..';
import { DEAL_PIPELINE_PAGE_EXECUTE_WORKFLOW_TRANSITION_MUTATION, UPDATE_INVESTMENT_SECURITY_STATUS_TRANSITION_MUTATION } from '../../queries';
import { AddButton } from '../addButton';
import { defaultListData, yseNoOptions } from '../constants';
import CreateStrategy from '../modals/createStrategy';
import CreateTag from '../modals/createTag';
import Dialog, { DialogStatus } from '../modals/dialog';
import { DynamicSelectWrapper } from '../radioWithDynamicSelects';
import { IListData } from '../types';
import { addStrategies, delStrategies, extractDirtyFields, isFieldRequiredByStatus, onChangeStrategies } from '../utils';

interface IForm {
  name: string;
  assetClassId: string;
  subAssetClassId: string;
  pacingType: InvestmentPacingType | null;
  pacingStartingYear: string | null;
  pacingStartingQuarter: string | null;
  ubtiBlocker: boolean;
  minimum: string;
  type: string;
  custodied: boolean;
}

const defaultFormValues: IForm = {
  name: '',
  assetClassId: '',
  subAssetClassId: '',
  pacingType: null,
  pacingStartingYear: null,
  pacingStartingQuarter: null,
  ubtiBlocker: null,
  minimum: '',
  type: '',
  custodied: null
};

const pacingTypeSelectOptions = [
  { label: 'Select', value: null },
  ...Object.values(InvestmentPacingType).map((type) => createSimpleSelectOption(type))
];

const findSubAssetClass = (assetClasses: AssetClassesQuery['assetClasses'], currentSubAssetClassId: string) => {
  for (const assetClass of assetClasses) {
    const subAssetClass = assetClass.subAssetClasses.find((subAssetClass) => subAssetClass.id === currentSubAssetClassId);
    if (subAssetClass) {
      return subAssetClass;
    }
  }
  return null;
};

const shouldShowPacingType = (
  assetClassId: string,
  subAssetClassId: string,
  assetClasses: AssetClassesQuery['assetClasses'],
  subAssetClasses: AssetClassesQuery['assetClasses'][number]['subAssetClasses']
) => {
  if (assetClassId && !subAssetClasses?.length) {
    const currentAssetClass = assetClasses.find((assetClass) => assetClass.id === assetClassId);
    return currentAssetClass && currentAssetClass.illiquid;
  }
  if (assetClassId && subAssetClassId && subAssetClasses?.length) {
    const currentSubAssetClass = findSubAssetClass(assetClasses, subAssetClassId);
    return currentSubAssetClass && currentSubAssetClass.illiquid;
  }
  return false;
};

interface BasicInfoModalPageProps {
  title: string;
  formData: IForm | null;
  investmentId?: string;
  investment?: {
    securityStatus?: Security_Status;
    tags?: string[];
    subAssetClasses?: string[];
    pacingStartingYear?: number;
    pacingStartingQuarter?: string;
  };
  investmentRefetch?: () => void;
  handleCloseEditModalPage: () => void;
}

export const BasicInfoModalPage = ({
  title,
  investmentId,
  formData,
  investment,
  investmentRefetch,
  handleCloseEditModalPage
}: BasicInfoModalPageProps) => {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const theme = useTheme();
  const goBack = useGoBack();
  const {
    handleSubmit,
    getValues,
    setValue,
    control,
    reset,
    watch,
    formState: { dirtyFields }
  } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogStatus, setDialogStatus] = useState<DialogStatus>();
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [assetClasses, setAssetClasses] = useState<AssetClassesQuery['assetClasses']>([]);
  const [tags, setTags] = useState<IListData>(defaultListData);
  const [strategies, setStrategies] = useState<IListData>(defaultListData);
  const [subAssetClasses, setSubAssetClasses] = useState<AssetClassesQuery['assetClasses'][number]['subAssetClasses']>([]);
  const [isFormValuesSet, setIsFormValuesSet] = useState(false);

  const [updateInvestment] = useMutation(UPDATE_INVESTMENT_MUTATION);
  const [executeWorkflowTransition] = useMutation(DEAL_PIPELINE_PAGE_EXECUTE_WORKFLOW_TRANSITION_MUTATION);
  const [updateInvestmentSecurityStatusTransition] = useMutation(UPDATE_INVESTMENT_SECURITY_STATUS_TRANSITION_MUTATION);

  const { loading: assetClassesLoading } = useQuery(GET_ASSET_CLASSES_QUERY, {
    onCompleted: ({ assetClasses }) => {
      setAssetClasses(assetClasses);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: tagsListData, loading: tagsListLoading } = useQuery(GET_CURRENT_TAGS_QUERY, {
    onCompleted: (data) => {
      const sortedTagsList = [...data.getCurrentTags.values].filter((tag: string) => tag !== '');
      setTags({ ...tags, list: sortedTagsList });
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: strategiesListData, loading: strategiesListLoading } = useQuery(GET_CURRENT_SUB_ASSET_CLASSES_QUERY, {
    onCompleted: (data) => {
      const sortedStrategiesList = [...data.getCurrentSubAssetClasses.values].filter((strategy: string) => strategy !== '');
      setStrategies({ ...strategies, list: sortedStrategiesList });
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    if (isFormValuesSet || !Object.keys(formData || {}).length) return;

    reset((defaultFormData) => ({ ...defaultFormData, ...(formData || {}) }));
    setTags({ ...tags, chosen: investment?.tags ?? [] });
    setStrategies({ ...strategies, chosen: investment?.subAssetClasses ?? [] });
    setIsFormValuesSet(true);
  }, [formData, investment]);

  useEffect(() => {
    setTags((prevTags) => {
      return {
        ...prevTags,
        list: prevTags.list.filter((tag) => !tags.chosen.includes(tag)).sort((a, b) => a.localeCompare(b))
      };
    });
  }, [tagsListData, tags.chosen]);

  useEffect(() => {
    setStrategies((prevStrategies) => {
      return {
        ...prevStrategies,
        list: prevStrategies.list.filter((strategy) => !strategies.chosen.includes(strategy)).sort((a, b) => a.localeCompare(b))
      };
    });
  }, [strategiesListData, strategies.chosen]);

  // const pacingType = useWatch({ control, name: 'pacingType' });

  // useEffect(() => {
  //   if (pacingType === InvestmentPacingType.EVERGREEN || !pacingType) {
  //     setValue('pacingStartingYear', null, { shouldDirty: true });
  //     setValue('pacingStartingQuarter', null, { shouldDirty: true });
  //   } else {
  //     const year = investment?.pacingStartingYear?.toString() || new Date().getFullYear().toString();
  //     const quarter = investment?.pacingStartingQuarter || 'Q1';
  //     setValue('pacingStartingYear', year, { shouldDirty: true });
  //     setValue('pacingStartingQuarter', quarter, { shouldDirty: true });
  //   }
  // }, [pacingType, investment]);

  useEffect(() => {
    if (!assetClasses?.length || !formData?.assetClassId) return;

    const currentAssetClass = assetClasses.find((assetClass) => assetClass.id === formData?.assetClassId);
    if (!currentAssetClass) return;

    if (currentAssetClass?.subAssetClasses?.length) {
      const currentSubAssetClass = currentAssetClass.subAssetClasses.find((subAssetClass) => subAssetClass.id === formData?.subAssetClassId);
      setSubAssetClasses(currentAssetClass?.subAssetClasses ?? []);

      if (formData?.pacingType) {
        setValue('pacingType', currentSubAssetClass && currentSubAssetClass?.illiquid ? formData?.pacingType : null, { shouldDirty: true });
        return;
      }
      // setValue('pacingType', currentSubAssetClass && currentSubAssetClass?.illiquid ? InvestmentPacingType.DRAWDOWN : null, { shouldDirty: true });
      return;
    }

    if (formData?.pacingType) {
      setValue('pacingType', currentAssetClass && currentAssetClass?.illiquid ? formData?.pacingType : null, { shouldDirty: true });
      return;
    }
    // setValue('pacingType', currentAssetClass && currentAssetClass?.illiquid ? InvestmentPacingType.DRAWDOWN : null, { shouldDirty: true });
  }, [assetClasses, formData]);

  const openModalWindow = (type: string) => {
    setModalWindow({ ...modalWindow, isOpen: true, type });
  };

  const onSubmit = async (data: IForm) => {
    if (!investmentId) return;
    try {
      setDialogStatus(DialogStatus.LOADING);
      setDialogOpen(true);
      const { pacingType, pacingStartingYear, pacingStartingQuarter } = data;
      const dirtyData = extractDirtyFields(data, dirtyFields);

      const investmentStrategies = [...strategies.chosen].filter((el) => el.length > 0).map((el) => el.trim());
      const investmentTags = [...tags.chosen].filter((el) => el.length > 0).map((el) => el.trim());

      await updateInvestment({
        variables: {
          data: {
            id: investmentId,
            ...dirtyData,
            subAssetClasses: investmentStrategies ?? [],
            tags: investmentTags ?? [],
            ...(dirtyFields?.minimum && { minimum: parseFloat(dirtyData.minimum) }),
            ...(dirtyFields?.pacingType && {
              pacingType: pacingType ? (getEnumKeyByValue(InvestmentPacingType, pacingType) as PacingType) : null
            }),
            ...(dirtyFields?.pacingStartingQuarter && pacingStartingQuarter && { pacingStartingQuarter: pacingStartingQuarter as PacingQuarter }),
            ...(dirtyFields?.pacingStartingYear && pacingStartingYear && { pacingStartingYear: parseInt(pacingStartingYear) })
          }
        }
      });

      if (location?.state?.updateSecurityStatus) {
        await updateInvestmentSecurityStatusTransition({
          variables: {
            data: {
              investmentId: location?.state?.investmentId,
              rawInput: location?.state?.rawInput
            }
          }
        });
      } else if (location?.state?.instanceId && location?.state?.transitionId) {
        await executeWorkflowTransition({
          variables: {
            data: {
              instanceId: location?.state?.instanceId,
              transitionId: location?.state?.transitionId
            }
          }
        });
      }

      investmentRefetch();
      setDialogStatus(DialogStatus.SUCCESS);
      setDialogOpen(true);
    } catch (error) {
      setDialogStatus(DialogStatus.FAILED);
      setDialogOpen(true);
    }
  };

  const handleClose = () => {
    if (location?.state?.source === 'dealPipeline' && (location?.state?.transitionId || location?.state?.updateSecurityStatus)) {
      goBack({ fallBack: '/dealPipeline' });
      return;
    }
    handleCloseEditModalPage();
  };

  const onCloseDialog = () => {
    setDialogOpen(false);
  };

  const onDialogSuccess = () => {
    reset();
    onCloseDialog();
    handleClose();
  };

  const reFetchDialog = () => {
    onSubmit(getValues());
  };

  if (assetClassesLoading || tagsListLoading || strategiesListLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <MainWrap>
      <Dialog isOpen={isDialogOpen} onClose={onCloseDialog} onSuccess={onDialogSuccess} reFetch={reFetchDialog} status={dialogStatus} />
      {modalWindow.isOpen && modalWindow.type === 'create-strategy' && (
        <CreateStrategy
          isOpen={modalWindow.isOpen}
          preFillValue={strategies.preFillValue || ''}
          setStrategies={setStrategies}
          onClose={() => setModalWindow({ type: 'create-strategy', isOpen: false })}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'create-tag' && (
        <CreateTag
          isOpen={modalWindow.isOpen}
          preFillValue={tags.preFillValue || ''}
          setTags={setTags}
          onClose={() => setModalWindow({ type: 'create-tag', isOpen: false })}
        />
      )}
      <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={''} />} />
      <PageTitle title={location?.state?.status ? _.startCase(location.state.status.toLowerCase()) : title ?? ''} />
      <PaddingWrap>
        <Form
          id="basicInfoForm"
          onSubmit={handleSubmit(onSubmit, () => {
            console.log(dirtyFields, 'dirtyFields');
            window.scrollTo(0, 0);
          })}
        >
          <FormFieldsWrapper>
            {!!location?.state?.transitionId && (
              <Banner
                icon={<InfoIcon fill={themeVar('font.base')} />}
                title="Required Fields Missing"
                description="Before updating this strategy, please provide and confirm the following settings."
                bgColor={themeVar('layer.2')}
                dividerColor={themeVar('border.base')}
                color={themeVar('font.base')}
              />
            )}
            <TwoColumnWrapper isMobile={isMobile}>
              <Controller
                name="name"
                control={control}
                rules={{
                  required: {
                    value: isFieldRequiredByStatus('name', location?.state?.status || investment?.securityStatus),
                    message: 'Name is required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    label="Name"
                    placeholder="Friendly name for use internally and with clients"
                    errorText={error?.message}
                    size="md"
                    isRequiredField={isFieldRequiredByStatus('name', location?.state?.status || investment?.securityStatus)}
                  />
                )}
              />
            </TwoColumnWrapper>
            <TwoColumnWrapper isMobile={isMobile}>
              <Controller
                name="assetClassId"
                control={control}
                rules={{
                  required: {
                    value: isFieldRequiredByStatus('assetClassId', location?.state?.status || investment?.securityStatus),
                    message: 'Asset Class is required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value || ''}
                    setValue={(newAssetClassId) => {
                      if (!newAssetClassId) {
                        setValue('subAssetClassId', '');
                        setSubAssetClasses([]);
                        setValue('pacingType', null, { shouldDirty: true });
                        setValue('pacingStartingYear', null, { shouldDirty: true });
                        setValue('pacingStartingQuarter', null, { shouldDirty: true });
                        onChange(newAssetClassId);
                        return;
                      }

                      if (newAssetClassId !== getValues('assetClassId')) {
                        const assetClass = assetClasses.find((assetClass) => assetClass.id === newAssetClassId);
                        setSubAssetClasses(assetClass?.subAssetClasses || []);
                        setValue('subAssetClassId', '');
                        onChange(newAssetClassId);
                        // if (!(assetClass?.subAssetClasses || []).length) {
                        //   const currentAssetClass = assetClasses.find((assetClass) => assetClass.id === newAssetClassId);
                        //   setValue('pacingType', currentAssetClass && currentAssetClass.illiquid ? InvestmentPacingType.DRAWDOWN : null, {
                        //     shouldDirty: true
                        //   });
                        //   return;
                        // }
                        setValue('pacingType', null, { shouldDirty: true });
                        setValue('pacingStartingYear', null, { shouldDirty: true });
                        setValue('pacingStartingQuarter', null, { shouldDirty: true });
                      }
                    }}
                    options={[
                      { value: null, label: 'Select' },
                      ...assetClasses.map((assetClass) => ({ value: assetClass.id, label: assetClass.name }))
                    ]}
                    label="Asset Class"
                    errorText={error?.message}
                    size="md"
                    isRequiredField={isFieldRequiredByStatus('assetClassId', location?.state?.status || investment?.securityStatus)}
                  />
                )}
              />
              {!!subAssetClasses.length && (
                <Controller
                  name="subAssetClassId"
                  control={control}
                  rules={{
                    required: {
                      value: isFieldRequiredByStatus('subAssetClassId', location?.state?.status || investment?.securityStatus),
                      message: 'Sub Asset Class is required'
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        value={value || ''}
                        setValue={(newSubAssetClassId) => {
                          onChange(newSubAssetClassId);

                          if (!newSubAssetClassId) {
                            setValue('pacingType', null, { shouldDirty: true });
                            setValue('pacingStartingYear', null, { shouldDirty: true });
                            setValue('pacingStartingQuarter', null, { shouldDirty: true });
                          }
                          // const currentSubAssetClass = findSubAssetClass(assetClasses, newSubAssetClassId);
                          // setValue('pacingType', currentSubAssetClass && currentSubAssetClass.illiquid ? InvestmentPacingType.DRAWDOWN : null, {
                          //   shouldDirty: true
                          // });
                        }}
                        options={[
                          { value: null, label: 'Select' },
                          ...subAssetClasses.map((subAssetClass) => ({ value: subAssetClass.id, label: subAssetClass.name }))
                        ]}
                        label="Sub Asset Class"
                        errorText={error?.message}
                        styleType={!subAssetClasses.length ? 'disabled' : 'default'}
                        size="md"
                        isRequiredField={isFieldRequiredByStatus('subAssetClassId', location?.state?.status || investment?.securityStatus)}
                      />
                    </>
                  )}
                />
              )}
            </TwoColumnWrapper>
            {shouldShowPacingType(watch('assetClassId'), watch('subAssetClassId'), assetClasses, subAssetClasses) && (
              <TwoColumnWrapper isMobile={isMobile}>
                <Line isMobile={isMobile}>
                  <Controller
                    name="pacingType"
                    control={control}
                    rules={{
                      required: {
                        value: isFieldRequiredByStatus('pacingType', location?.state?.status || investment?.securityStatus),
                        message: 'Pacing Type is required'
                      }
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        value={value || ''}
                        setValue={onChange}
                        options={pacingTypeSelectOptions}
                        label="Pacing Type"
                        errorText={error?.message}
                        size="md"
                        isRequiredField={isFieldRequiredByStatus('pacingType', location?.state?.status || investment?.securityStatus)}
                        tooltip="Pacing model forecasts will assume that evergreen funds are held indefinitely and that all investor capital is transferred as part of the initial subscription."
                        tooltipWidth={300}
                      />
                    )}
                  />
                </Line>
                {watch('pacingType') === InvestmentPacingType.DRAWDOWN && (
                  <Line isMobile={isMobile}>
                    <Controller
                      name="pacingStartingYear"
                      control={control}
                      rules={{
                        required: {
                          value: isFieldRequiredByStatus('pacingStartingYear', location?.state?.status || investment?.securityStatus),
                          message: 'Pacing Starting Year is required'
                        },
                        pattern: {
                          value: /^\d{4}$/,
                          message: 'Must be four digits only'
                        }
                      }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.target.value);
                          }}
                          label="Pacing Starting Year"
                          placeholder="Enter a year using four digits"
                          errorText={error?.message}
                          size="md"
                          tooltipWidth={300}
                          tooltip="Provide a year and quarter that roughly corresponds to the inception of the fund.  This is an input to the pacing model and cash flow forecasts."
                          isRequiredField={isFieldRequiredByStatus('pacingStartingYear', location?.state?.status || investment?.securityStatus)}
                        />
                      )}
                    />
                    <Controller
                      name="pacingStartingQuarter"
                      control={control}
                      rules={{
                        required: {
                          value: isFieldRequiredByStatus('pacingStartingQuarter', location?.state?.status || investment?.securityStatus),
                          message: 'Pacing Starting Quarter is required'
                        }
                      }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Select
                          value={value || ''}
                          setValue={onChange}
                          options={['Q1', 'Q2', 'Q3', 'Q4'].map((item) => createSimpleSelectOption(item))}
                          label="Pacing Starting Quarter"
                          errorText={error?.message}
                          size="md"
                          isRequiredField={isFieldRequiredByStatus('pacingStartingQuarter', location?.state?.status || investment?.securityStatus)}
                          tooltip="Provide a year and quarter that roughly corresponds to the inception of the fund.  This is an input to the pacing model and cash flow forecasts."
                          tooltipWidth={300}
                        />
                      )}
                    />
                  </Line>
                )}
              </TwoColumnWrapper>
            )}
            <TwoColumnWrapper isMobile={isMobile}>
              <div>
                <Label tooltip="Used for categorization and automated allocation rules" tooltipWidth={250} size="md">
                  Sectors
                </Label>
                {strategies.chosen.map((el, index) => (
                  <DynamicSelectWrapper key={`strategies-${index}`} loading={index === strategies.chosen.length - 1 && strategies.duplicate ? 1 : 0}>
                    <Select
                      value={strategies.chosen[index]}
                      previewValue={strategies.chosen[index]}
                      setValue={(value) => onChangeStrategies(value, index, strategies, setStrategies)}
                      saveSearchResult={(value) => setStrategies((prev) => ({ ...prev, preFillValue: value }))}
                      options={strategies.list.map((strategy) => createSimpleSelectOption(strategy))}
                      styleType={
                        strategies.chosen[strategies.chosen.length - 1] !== el ||
                        strategies.chosen[strategies.chosen.length - 1] === strategies.duplicate
                          ? 'disabled'
                          : 'default'
                      }
                      size="md"
                      withSearch
                      additionalOption={
                        <AdditionalOptionWrapper onClick={() => openModalWindow('create-strategy')}>
                          <PlusIcon width={18} height={18} fill={theme.font.action} />
                          <AddText>Create New</AddText>
                        </AdditionalOptionWrapper>
                      }
                    />
                    <DelItemWrap onClick={() => delStrategies(el, index, strategies, setStrategies)}>
                      <CloseInfoIcon width={21} height={20} fill={theme.font.action} />
                    </DelItemWrap>
                  </DynamicSelectWrapper>
                ))}
                {!!strategies.list.length && strategies.chosen[strategies.chosen.length - 1] !== '' && (
                  <AddButton text="Add" onClick={() => addStrategies(strategies, setStrategies)} />
                )}
              </div>
              <div>
                <Label tooltip="Used for categorization (informational field only)" size="md">
                  Tags
                </Label>
                {tags.chosen.map((el, index) => (
                  <DynamicSelectWrapper key={`tags-${index}`} loading={index === tags.chosen.length - 1 && tags.duplicate ? 1 : 0}>
                    <Select
                      value={tags.chosen[index]}
                      previewValue={tags.chosen[index]}
                      setValue={(value) => onChangeStrategies(value, index, tags, setTags)}
                      saveSearchResult={(value) => setTags((prev) => ({ ...prev, preFillValue: value }))}
                      options={tags.list.map((tag) => createSimpleSelectOption(tag))}
                      styleType={
                        tags.chosen[tags.chosen.length - 1] !== el || tags.chosen[tags.chosen.length - 1] === tags.duplicate ? 'disabled' : 'default'
                      }
                      size="md"
                      withSearch
                      additionalOption={
                        <AdditionalOptionWrapper onClick={() => openModalWindow('create-tag')}>
                          <PlusIcon width={18} height={18} fill={theme.font.action} />
                          <AddText>Create New</AddText>
                        </AdditionalOptionWrapper>
                      }
                    />
                    <DelItemWrap onClick={() => delStrategies(el, index, tags, setTags)}>
                      <CloseInfoIcon width={21} height={20} fill={theme.font.action} />
                    </DelItemWrap>
                  </DynamicSelectWrapper>
                ))}
                {!!tags.list.length && tags.chosen[tags.chosen.length - 1] !== '' && (
                  <AddButton text="Add" onClick={() => addStrategies(tags, setTags)} />
                )}
              </div>
            </TwoColumnWrapper>
            {(SECURITY_STATUS[location?.state?.status as keyof typeof SECURITY_STATUS] === SECURITY_STATUS.SEEKING_INTEREST ||
              SECURITY_STATUS[location?.state?.status as keyof typeof SECURITY_STATUS] === SECURITY_STATUS.OPEN) && (
              <>
                <TwoColumnWrapper isMobile={isMobile}>
                  <Controller
                    name="minimum"
                    control={control}
                    rules={{
                      required: {
                        value: isFieldRequiredByStatus('minimum', location?.state?.status || investment?.securityStatus),
                        message: 'Minimum is required'
                      }
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        value={value ? formatMaskedDollarInputValue(value) : ''}
                        onChange={(e) => {
                          const formattedValue = formatDollarInputValue(e.target.value);
                          onChange(formattedValue);
                        }}
                        label="Minimum Investor Commitment"
                        errorText={error?.message}
                        withDollarSign
                        size="md"
                        isRequiredField={isFieldRequiredByStatus('minimum', location?.state?.status || investment?.securityStatus)}
                        tooltipWidth={300}
                        tooltip="Provide the investment minimum. Entities with insufficient capacity will be flagged based on this value."
                      />
                    )}
                  />
                  <Controller
                    name="type"
                    control={control}
                    rules={{
                      required: {
                        value: isFieldRequiredByStatus('type', location?.state?.status || investment?.securityStatus),
                        message: 'Type is a required field'
                      }
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        value={value}
                        setValue={onChange}
                        options={[createSimpleSelectOption('Fund'), createSimpleSelectOption('Direct')]}
                        errorText={error?.message}
                        size="md"
                        label="Type"
                        isRequiredField={isFieldRequiredByStatus('type', location?.state?.status || investment?.securityStatus)}
                      />
                    )}
                  />
                </TwoColumnWrapper>
                <TwoColumnWrapper isMobile={isMobile}>
                  <div>
                    <Label
                      size="md"
                      tooltip="Answer Yes if this strategy is suitable for tax exempt investors. Either there is a UBTI Blocker in place, such as an offshore vehicle, or there is no risk of generating UBTI"
                      tooltipWidth={300}
                      required={isFieldRequiredByStatus('ubtiBlocker', location?.state?.status || investment?.securityStatus)}
                    >
                      Tax Exempt Suitable
                    </Label>
                    <Controller
                      name="ubtiBlocker"
                      control={control}
                      rules={{
                        validate: {
                          isBoolean: (value) => {
                            if (isFieldRequiredByStatus('ubtiBlocker', location?.state?.status || investment?.securityStatus)) {
                              return typeof value === 'boolean' || 'Tax Exempt Suitable is required';
                            }
                            return true;
                          }
                        }
                      }}
                      render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <>
                          <TogglesWrap isMobile={isMobile}>
                            {yseNoOptions.map((option, index) => (
                              <RadioButton
                                key={option + index}
                                name="ubtiBlocker"
                                radioButtonText={option}
                                selectedOption={value === null ? '' : value ? yseNoOptions[0] : yseNoOptions[1]}
                                handleRadioChange={(event) => onChange(event.target.value === yseNoOptions[0])}
                              />
                            ))}
                          </TogglesWrap>
                          {error?.message && <ErrorText size="md">{error.message}</ErrorText>}
                        </>
                      )}
                    />
                  </div>
                </TwoColumnWrapper>
                <TwoColumnWrapper isMobile={isMobile}>
                  <div>
                    <Label
                      size="md"
                      tooltipWidth={300}
                      tooltip="Some investor types (e.g., IRAs and DAFs) require that a fund is registered with a custodian in a way that permits these investor types to invest. Use this flag to indicate that this requirement is satisfied for the relevant investors (or not)."
                      required={isFieldRequiredByStatus('custodied', location?.state?.status || investment?.securityStatus)}
                    >
                      Custodied
                    </Label>
                    <Controller
                      name="custodied"
                      control={control}
                      rules={{
                        validate: {
                          isBoolean: (value) => {
                            if (isFieldRequiredByStatus('custodied', location?.state?.status || investment?.securityStatus)) {
                              return typeof value === 'boolean' || 'Custodied is required';
                            }
                            return true;
                          }
                        }
                      }}
                      render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <>
                          <TogglesWrap isMobile={isMobile}>
                            {yseNoOptions.map((option, index) => (
                              <RadioButton
                                key={option + index}
                                name="custodied"
                                radioButtonText={option}
                                selectedOption={value === null ? '' : value ? yseNoOptions[0] : yseNoOptions[1]}
                                handleRadioChange={(event) => onChange(event.target.value === yseNoOptions[0])}
                              />
                            ))}
                          </TogglesWrap>
                          {error?.message && <ErrorText size="md">{error.message}</ErrorText>}
                        </>
                      )}
                    />
                  </div>
                </TwoColumnWrapper>
              </>
            )}
          </FormFieldsWrapper>
          <FormFooter onCancel={handleClose} disableSaveButton={false} />
        </Form>
      </PaddingWrap>
    </MainWrap>
  );
};

const Form = styled.form`
  padding: 50px 0;
  width: 100%;
  background: ${({ theme }) => theme.layer.base};
`;

const AdditionalOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const AddText = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${({ theme }) => theme.font.action};
`;

const Line = styled.span<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  width: 100%;
  gap: 30px;
  margin-top: 4px;
`;

const TogglesWrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '0')};
`;

const DelItemWrap = styled.div`
  position: relative;
  top: 3px;
  padding-left: 15px;
  cursor: pointer;
`;
